/**
 * Top Committees on the individual donor page as a Sankey: the donor, then
 * committee category (Super PACs, party, campaigns, PACs), then each committee
 * (sites#413). No ECharts here: the chart comes from /js/money-flow.js, loaded
 * on demand (vendor-clients.ts loadMoneyFlow).
 */

import { Constants } from '@electinfo/common';
import { FLOW_LAYOUT, flowChartHeight, type FlowLink, type FlowNode } from './flow-types';

export interface IndividualCommitteeRow {
  id: string;
  name: string | null;
  committeeType: string | null;
  party?: string | null;
  affinityDem?: number | null;
  affinityRep?: number | null;
  /** Net of paired refunds. */
  amount: number | null;
  count: number | null;
}

// FEC committee type codes grouped for the middle column; anything else is "Other committees".
const GROUPS: Array<[string, string[]]> = [
  ['Super PACs', ['O', 'U']],
  ['Party committees', ['X', 'Y', 'Z']],
  ['Presidential campaigns', ['P']],
  ['Senate campaigns', ['S']],
  ['House campaigns', ['H']],
  ['PACs', ['N', 'Q', 'V', 'W']],
];

export function committeeGroup(code: string | null | undefined): string {
  const c = (code || '').trim().toUpperCase();
  return GROUPS.find(([, codes]) => codes.includes(c))?.[0] ?? 'Other committees';
}

/** "Super PAC" for O; unknown codes as given; null when there is none. */
export function committeeTypeLabel(code: string | null | undefined): string | null {
  const c = (code || '').trim().toUpperCase();
  if (!c) return null;
  return (Constants.committeeTypeNames as Record<string, string>)[c] ?? c;
}

/** Bands at least this tall, so every committee can carry a label. */
const MIN_BAND_PX = FLOW_LAYOUT.minLabelPx + 1;

/**
 * The smallest drawn amount that gives a band MIN_BAND_PX in the committee
 * column. Raising small flows to it grows the column total, so iterate; this
 * converges while the floored bands take less than the whole column.
 */
export function displayFloor(amounts: number[]): number {
  const n = amounts.length;
  const plot = flowChartHeight(n) - FLOW_LAYOUT.top - FLOW_LAYOUT.bottom - FLOW_LAYOUT.nodeGap * Math.max(0, n - 1);
  if (plot <= 0 || n === 0) return 0;
  let total = amounts.reduce((t, a) => t + a, 0);
  let floor = 0;
  for (let i = 0; i < 12; i++) {
    floor = (MIN_BAND_PX * total) / plot;
    total = amounts.reduce((t, a) => t + Math.max(a, floor), 0);
  }
  return floor;
}

const node = (n: Partial<FlowNode> & Pick<FlowNode, 'id' | 'side' | 'kind' | 'name'>): FlowNode => ({
  entityId: null, fecId: null, party: null, affinityDem: null, affinityRep: null, amount: null, count: null, otherCount: null, ...n,
});

/**
 * Donor (focal, left) → category (mid) → committee (out), ordered by amount as
 * the table is. Flows too small to see are drawn at a floor width; `widened`
 * says so, for the chart caption. Tooltips keep the real amounts.
 */
export function individualCommitteeFlow(
  individual: { id: string; name: string },
  rows: IndividualCommitteeRow[],
): { nodes: FlowNode[]; links: FlowLink[]; widened: boolean } {
  const kept = rows.filter((r) => Number.isFinite(Number(r.amount)) && Number(r.amount) > 0);
  const floor = displayFloor(kept.map((r) => Number(r.amount)));
  const focal = node({ id: 'focal', side: 'focal', kind: 'Individual', entityId: individual.id, name: individual.name });
  const groups = new Map<string, { node: FlowNode; count: number }>();
  const committees: FlowNode[] = [];
  const links: FlowLink[] = [];
  let widened = false;

  for (const r of kept) {
    const amount = Number(r.amount);
    const display = Math.max(amount, floor);
    if (display > amount) widened = true;
    const label = committeeGroup(r.committeeType);
    let g = groups.get(label);
    if (!g) {
      g = { node: node({ id: `mid:${label}`, side: 'mid', kind: 'Group', name: label, amount: 0, display: 0 }), count: 0 };
      groups.set(label, g);
    }
    g.node.amount! += amount;
    g.node.display! += display;
    g.count += Number(r.count) || 0;
    const id = `out:${r.id}`;
    committees.push(node({
      id, side: 'out', kind: 'Committee', entityId: r.id, name: r.name || 'Unknown committee',
      party: r.party ?? null, affinityDem: r.affinityDem ?? null, affinityRep: r.affinityRep ?? null,
      amount, display, count: r.count,
    }));
    links.push({ source: g.node.id, target: id, amount, display, count: r.count, flow: 'contribution' });
  }

  const mids = [...groups.values()].sort((a, b) => b.node.amount! - a.node.amount!);
  // Committees in category order (amount within it), so links do not cross; the table keeps amount order.
  const rank = new Map(mids.map((m, i) => [m.node.id, i]));
  const groupOf = new Map(links.map((l) => [l.target, l.source]));
  committees.sort((a, b) => rank.get(groupOf.get(a.id)!)! - rank.get(groupOf.get(b.id)!)! || b.amount! - a.amount!);
  const midLinks: FlowLink[] = mids.map(({ node: m, count }) => ({
    source: focal.id, target: m.id, amount: m.amount!, display: m.display, count: count || null, flow: 'contribution',
  }));
  return { nodes: [focal, ...mids.map((m) => m.node), ...committees], links: [...midLinks, ...links], widened };
}
